import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Upload, X, Trash2, FileBox, Sparkles, Loader2, Check, AlertTriangle } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { SearchableSelect } from "./SearchableSelect";
import { Odontogram } from "./Odontogram";
import { RESTORATION_TYPES, MATERIALS, isImplantType, teethList, toothLabel } from "../lib/dental";
import { CAD_ACCEPT, isCadFile } from "../lib/cad";
import { suggestPontics, validateBridge } from "../services/gemini";
import { toast } from "sonner";

// Editor for a single restoration inside CreateCase.
export const RestorationForm = ({ index, value, onChange, onRemove, canRemove, numbering }) => {
  const { t, i18n } = useTranslation();
  const [suggested, setSuggested] = useState([]); // FDI strings pending confirmation
  const [suggestReason, setSuggestReason] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [bridgeWarnings, setBridgeWarnings] = useState([]);
  const [validating, setValidating] = useState(false);

  const set = (k, v) => {
    if (k === "type") setSuggested([]); // stale suggestion once the type changes
    onChange({ ...value, [k]: v });
  };
  const setImplant = (k, v) => onChange({ ...value, implant: { ...value.implant, [k]: v } });

  const typeLabel = (v) => t(`case.restorationTypes.${v}`, v);
  const materialLabel = (v) => t(`case.materials.${v}`, v);
  const showImplant = isImplantType(value.type);

  const isBridge = value.type === "bridge" || value.type === "implant_bridge";
  const abutmentCount = teethList(value.teeth).filter((x) => x.state === "abutment").length;
  const canSuggest = isBridge && abutmentCount >= 2;

  const runSuggest = async () => {
    setSuggesting(true);
    setSuggested([]);
    setSuggestReason("");
    try {
      const res = await suggestPontics({
        restoration_type: value.type,
        teeth: value.teeth,
        tooth_numbering: numbering,
      });
      if (res.status === "ok" && Array.isArray(res.suggestions) && res.suggestions.length) {
        setSuggested(res.suggestions.map(String));
        setSuggestReason((res.reason && (numbering === "universal" ? res.reason.en : res.reason.es)) || "");
      } else if (res.status === "ok") {
        toast.info(t("restoration.noPonticsFound"));
      } else {
        toast.warning(res.error_type === "quota" ? t("ai.quotaError") : t("restoration.suggestError"));
      }
    } catch (e) {
      toast.error(t("restoration.suggestError"));
    } finally {
      setSuggesting(false);
    }
  };

  const applySuggestion = () => {
    const teeth = { ...value.teeth };
    suggested.forEach((fdi) => { teeth[String(fdi)] = "pontic"; });
    onChange({ ...value, teeth });
    setSuggested([]);
    setSuggestReason("");
    toast.success(t("restoration.suggestionApplied"));
  };

  const discardSuggestion = () => {
    setSuggested([]);
    setSuggestReason("");
  };

  const addCad = (fileList) => {
    const incoming = Array.from(fileList || []);
    const valid = incoming.filter(isCadFile);
    if (valid.length !== incoming.length) toast.error(t("cad.invalidType"));
    if (valid.length) set("cadFiles", [...value.cadFiles, ...valid]);
  };
  const removeCad = (i) => set("cadFiles", value.cadFiles.filter((_, j) => j !== i));
  const removeExistingCad = (i) => set("existingCad", value.existingCad.filter((_, j) => j !== i));
  const totalCad = value.existingCad.length + value.cadFiles.length;

  // Puente Inteligente Ampliado: for a bridge/implant bridge, Gemini validates the
  // selected structure and only WARNS (never modifies teeth). Debounced on changes.
  const teethKey = JSON.stringify(value.teeth || {});
  const validateTimer = useRef(null);
  const validateReqId = useRef(0);
  useEffect(() => {
    if (validateTimer.current) clearTimeout(validateTimer.current);
    // Invalidate any in-flight request so a slow/stale response can't overwrite state.
    validateReqId.current += 1;
    const reqId = validateReqId.current;
    if (!isBridge || teethList(value.teeth).length === 0) {
      setBridgeWarnings([]);
      setValidating(false);
      return undefined;
    }
    setValidating(true);
    validateTimer.current = setTimeout(async () => {
      try {
        const res = await validateBridge({
          restoration_type: value.type,
          teeth: value.teeth,
          tooth_numbering: numbering,
        });
        if (reqId !== validateReqId.current) return; // stale response, ignore
        if (res.status === "ok") {
          const lang = i18n.language?.split("-")[0] === "es" ? "es" : "en";
          setBridgeWarnings((res.warnings && res.warnings[lang]) || []);
        } else {
          setBridgeWarnings([]);
        }
      } catch (e) {
        if (reqId === validateReqId.current) setBridgeWarnings([]);
      } finally {
        if (reqId === validateReqId.current) setValidating(false);
      }
    }, 900);
    return () => { if (validateTimer.current) clearTimeout(validateTimer.current); };
  }, [isBridge, value.type, teethKey, numbering, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4 rounded-xl border border-border bg-card p-4" data-testid={`restoration-${index}`}>
      <div className="flex items-center justify-between">
        <h4 className="font-heading text-sm font-semibold tracking-tight">
          {t("restoration.item", { n: index + 1 })}
        </h4>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
            data-testid={`restoration-remove-${index}`}
          >
            <Trash2 className="h-3.5 w-3.5" /> {t("restoration.remove")}
          </button>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>{t("case.restorationType")}</Label>
          <SearchableSelect
            value={value.type}
            onValueChange={(v) => set("type", v)}
            options={RESTORATION_TYPES}
            labelFor={typeLabel}
            placeholder={t("restoration.pickType")}
            searchPlaceholder={t("restoration.searchType")}
            testid={`restoration-type-${index}`}
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t("case.material")}</Label>
          <SearchableSelect
            value={value.material}
            onValueChange={(v) => set("material", v)}
            options={MATERIALS}
            labelFor={materialLabel}
            placeholder={t("restoration.pickMaterial")}
            searchPlaceholder={t("restoration.searchMaterial")}
            testid={`restoration-material-${index}`}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label>{t("restoration.teeth")}</Label>
          {canSuggest && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={runSuggest}
              disabled={suggesting}
              data-testid={`suggest-pontics-${index}`}
            >
              {suggesting ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
              {t("restoration.suggestPontics")}
            </Button>
          )}
        </div>
        {suggested.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid={`pontic-suggestion-${index}`}>
            <p className="text-sm text-amber-900">
              {t("restoration.suggestion")}: <span className="font-semibold">{suggested.map((fdi) => toothLabel(fdi, numbering)).join(", ")}</span>
              {suggestReason ? <span className="block text-xs text-amber-700">{suggestReason}</span> : null}
            </p>
            <div className="flex gap-2">
              <Button type="button" size="sm" className="h-8 rounded-full bg-amber-600 text-white hover:bg-amber-700" onClick={applySuggestion} data-testid={`apply-pontics-${index}`}>
                <Check className="mr-1 h-3.5 w-3.5" /> {t("restoration.applySuggestion")}
              </Button>
              <Button type="button" size="sm" variant="ghost" className="h-8 rounded-full" onClick={discardSuggestion} data-testid={`discard-pontics-${index}`}>
                {t("restoration.discardSuggestion")}
              </Button>
            </div>
          </div>
        )}
        <Odontogram
          value={value.teeth}
          onChange={(teeth) => set("teeth", teeth)}
          numbering={numbering}
          suggested={suggested}
          testid={`odontogram-${index}`}
        />
        {validating && (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`bridge-validating-${index}`}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("restoration.validatingBridge")}
          </p>
        )}
        {bridgeWarnings.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3" data-testid={`bridge-warning-${index}`}>
            <p className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-amber-900">
              <AlertTriangle className="h-4 w-4" /> {t("restoration.bridgeWarningsTitle")}
            </p>
            <ul className="space-y-1">
              {bridgeWarnings.map((w, i) => (
                <li key={i} className="text-sm text-amber-800" data-testid={`bridge-warning-${index}-item-${i}`}>• {w}</li>
              ))}
            </ul>
            <p className="mt-2 text-[11px] text-amber-700">{t("restoration.bridgeWarningHint")}</p>
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <Label>{t("case.shade")}</Label>
        <Input value={value.shade} onChange={(e) => set("shade", e.target.value)} placeholder={t("case.shadePlaceholder")} data-testid={`restoration-shade-${index}`} />
      </div>

      <div className="space-y-1.5">
        <Label>{t("case.clinicalInstructions")}</Label>
        <Textarea rows={3} value={value.clinicalInstructions} onChange={(e) => set("clinicalInstructions", e.target.value)} placeholder={t("case.clinicalInstructionsPlaceholder")} data-testid={`restoration-instructions-${index}`} />
      </div>

      {showImplant && (
        <div className="space-y-3 rounded-lg border border-sky-200 bg-sky-50/60 p-3" data-testid={`implant-section-${index}`}>
          <p className="text-sm font-semibold text-sky-900">{t("restoration.implantInfo")}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("restoration.retainedBy")}</Label>
              <SearchableSelect
                value={value.implant.retainedBy}
                onValueChange={(v) => setImplant("retainedBy", v)}
                options={[{ value: "cement", common: true }, { value: "screw", common: true }]}
                labelFor={(v) => t(`restoration.${v}`, v)}
                placeholder={t("restoration.retainedBy")}
                searchPlaceholder={t("restoration.retainedBy")}
                testid={`implant-retained-${index}`}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("restoration.brand")}</Label>
              <Input value={value.implant.brand} onChange={(e) => setImplant("brand", e.target.value)} placeholder="e.g. Straumann BLX" data-testid={`implant-brand-${index}`} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("restoration.connection")}</Label>
              <Input value={value.implant.connection} onChange={(e) => setImplant("connection", e.target.value)} placeholder="e.g. Conical / internal hex" data-testid={`implant-connection-${index}`} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("restoration.tiBase")}</Label>
              <Input value={value.implant.tiBase} onChange={(e) => setImplant("tiBase", e.target.value)} placeholder="e.g. Ti-base ref." data-testid={`implant-tibase-${index}`} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>{t("restoration.scanbody")}</Label>
              <Input value={value.implant.scanbody} onChange={(e) => setImplant("scanbody", e.target.value)} placeholder="e.g. Scanbody brand/ref" data-testid={`implant-scanbody-${index}`} />
            </div>
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <Label className="flex items-center gap-1.5"><FileBox className="h-4 w-4" /> {t("restoration.cadFiles")}</Label>
        <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-secondary/30 p-3 text-sm text-muted-foreground hover:border-slate-300">
          <Upload className="h-4 w-4" /> {totalCad ? t("cad.addMore") : t("case.uploadCad")}
          <input type="file" accept={CAD_ACCEPT} multiple className="hidden" onChange={(e) => { addCad(e.target.files); e.target.value = ""; }} data-testid={`restoration-cad-input-${index}`} />
        </label>
        {totalCad > 0 && (
          <ul className="space-y-1" data-testid={`restoration-cad-list-${index}`}>
            {value.existingCad.map((f, i) => (
              <li key={`ec-${i}`} className="flex items-center justify-between gap-2 rounded-md border border-border bg-background px-2.5 py-1.5">
                <span className="truncate font-mono text-xs text-muted-foreground">{f.name}</span>
                <button type="button" onClick={() => removeExistingCad(i)} aria-label={t("cad.remove")}><X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" /></button>
              </li>
            ))}
            {value.cadFiles.map((f, i) => (
              <li key={`nc-${i}`} className="flex items-center justify-between gap-2 rounded-md border border-border bg-background px-2.5 py-1.5">
                <span className="truncate font-mono text-xs text-muted-foreground">{f.name}</span>
                <button type="button" onClick={() => removeCad(i)} aria-label={t("cad.remove")}><X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
